import { useMemo } from 'react'
import { Badge, Box, Flex, SegmentedControl, Switch, Text } from '@radix-ui/themes'
import type { TRPCOutputs } from '~/utils/trpc'
import { countByCompatibility, countByStage } from '~/features/installations/service'
import { StageProgressBar } from '~/features/installations/components/StageProgressBar'
import { CompatibilityProgressBar } from '~/features/installations/components/CompatibilityProgressBar'
import { INSTALLATION_VIEWS, installationViewLabel, type InstallationView } from '../constants'
import { computeDeliveryHealth, computeTimingRows } from '../timeline'
import { InstallationStatusTable } from './InstallationStatusTable'
import { InstallationTimelineTable } from './InstallationTimelineTable'
import { InstallationHardwareTable } from './InstallationHardwareTable'
import { InstallationRetiredTable } from './InstallationRetiredTable'
import { DeliveryHealthPanel } from './DeliveryHealthPanel'

type Installation = TRPCOutputs['ops']['stationInstallations']['list'][number]

interface OrgInstallationsTabProps {
  installations: Installation[]
  retired: Installation[]
  view: InstallationView
  stalledOnly: boolean
  stalledCount: number
  openTicketCounts: Record<string, number>
  onViewChange: (view: InstallationView) => void
  onStalledOnlyChange: (next: boolean) => void
  onRowClick: (installationId: number) => void
}

export const OrgInstallationsTab = ({
  installations,
  retired,
  view,
  stalledOnly,
  stalledCount,
  openTicketCounts,
  onViewChange,
  onStalledOnlyChange,
  onRowClick
}: OrgInstallationsTabProps) => {
  const rows = useMemo(() => computeTimingRows(installations), [installations])
  // Reports on the rows on screen, so the stalled-only filter narrows this with the table.
  const health = useMemo(() => computeDeliveryHealth(rows), [rows])

  // The other three views analyse work still in play — progress bars, delivery health — which a
  // retired installation would distort. It gets its own frame instead of a filter inside theirs.
  const isRetiredView = view === 'retired'

  return (
    <Flex direction="column" gap="4">
      <Flex align="center" justify="between" gap="3" wrap="wrap">
        <SegmentedControl.Root value={view} onValueChange={(next) => onViewChange(next as InstallationView)}>
          {INSTALLATION_VIEWS.map((option) => (
            <SegmentedControl.Item key={option} value={option}>
              <Flex align="center" gap="2">
                {installationViewLabel[option]}
                {option === 'retired' && retired.length > 0 && (
                  <Badge color="gray" variant="soft" size="1">
                    {retired.length}
                  </Badge>
                )}
              </Flex>
            </SegmentedControl.Item>
          ))}
        </SegmentedControl.Root>

        {!isRetiredView && (stalledCount > 0 || stalledOnly) && (
          <Text as="label" size="1" color="gray">
            <Flex align="center" gap="2">
              <Switch size="1" color="red" checked={stalledOnly} onCheckedChange={onStalledOnlyChange} />
              Stalled only ({stalledCount})
            </Flex>
          </Text>
        )}
      </Flex>

      {isRetiredView ? (
        retired.length === 0 ? (
          <Box py="6">
            <Text color="gray">No retired installations for this organization.</Text>
          </Box>
        ) : (
          <Box className="overflow-x-auto">
            <InstallationRetiredTable installations={retired} onRowClick={onRowClick} />
          </Box>
        )
      ) : installations.length === 0 ? (
        <Box py="6">
          <Text color="gray">
            {stalledOnly ? 'No stalled installations.' : 'No installations for this organization.'}
          </Text>
        </Box>
      ) : (
        <>
          {view === 'status' && (
            <StageProgressBar byStage={countByStage(installations)} total={installations.length} />
          )}
          {view === 'hardware' && (
            <CompatibilityProgressBar
              byCompatibility={countByCompatibility(installations)}
              total={installations.length}
            />
          )}
          {view === 'timeline' && <DeliveryHealthPanel health={health} />}

          <Box className="overflow-x-auto">
            {view === 'status' ? (
              <InstallationStatusTable
                installations={installations}
                openTicketCounts={openTicketCounts}
                onRowClick={onRowClick}
              />
            ) : view === 'hardware' ? (
              <InstallationHardwareTable installations={installations} onRowClick={onRowClick} />
            ) : (
              <InstallationTimelineTable rows={rows} onRowClick={onRowClick} />
            )}
          </Box>
        </>
      )}
    </Flex>
  )
}
